import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DataTableAdvanced,
  type DataTableColumn,
  type DataTableFilter,
} from '@/components/ui/data-table-advanced'
import { hierarchyService, type HierarchyPairing } from '@/services/hierarchy'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { Network, Building2, History, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

/**
 * SUB-3.13 · FR62. Every parent and sub-account pairing on the platform, with
 * allowance and current usage.
 *
 * Superadmin only, and gated in the database rather than by this route: the
 * sidebar entry being hidden is not a control, which is the FR19 lesson that
 * moved these surfaces out of joules-web in the first place.
 */

/**
 * ⚠️ NOT COMPUTED FROM THE ROWS. `used` is `sub_account_usage()`'s figure and
 * excludes archived client accounts (FR67), while this table lists them. A
 * parent with three clients of which one is archived reads "2 of 5" beside
 * three rows, and that is correct: the archived one does not consume the
 * allowance but still blocks disabling sub-accounts.
 */
const usageLabel = (row: HierarchyPairing): string => {
  if (row.unlimited) return `${row.used} of unlimited`
  return `${row.used} of ${row.allowance ?? 0}`
}

/** A flat row per pairing, plus the searchable fields the table needs. */
interface PairingRow extends HierarchyPairing {
  id: string
  usage: string
}

export const Hierarchy: React.FC = () => {
  const navigate = useNavigate()
  const [pairings, setPairings] = useState<HierarchyPairing[]>([])
  const [loading, setLoading] = useState(true)

  const fetchPairings = async () => {
    try {
      setLoading(true)
      setPairings(await hierarchyService.listRelationships())
    } catch (err: unknown) {
      console.error('Failed to load the hierarchy:', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail
      toast.error(detail || 'Could not load the account hierarchy')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPairings()
  }, [])

  const rows: PairingRow[] = useMemo(
    () =>
      pairings.map((p) => ({
        ...p,
        // Composite, because a parent with no children has a null child id and
        // a parent appears once per child.
        id: `${p.parent_tenant_id}:${p.child_tenant_id ?? 'none'}`,
        usage: usageLabel(p),
      })),
    [pairings]
  )

  // Counts from the rows, which is safe: these are counts OF THE LISTING, not
  // of the allowance. The allowance figure is never derived here.
  const providerCount = useMemo(
    () => new Set(pairings.map((p) => p.parent_tenant_id)).size,
    [pairings]
  )
  const clientCount = useMemo(
    () => pairings.filter((p) => p.child_tenant_id).length,
    [pairings]
  )
  const atLimitCount = useMemo(
    () =>
      new Set(
        pairings.filter((p) => p.at_limit).map((p) => p.parent_tenant_id)
      ).size,
    [pairings]
  )

  const columns: DataTableColumn<PairingRow>[] = [
    {
      accessorKey: 'parent_company_name',
      header: 'Provider',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.parent_company_name}</span>
          {!row.original.parent_sub_accounts_enabled && (
            <Badge variant="outline" className="text-xs">
              disabled
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'child_company_name',
      header: 'Client account',
      cell: ({ row }) =>
        row.original.child_tenant_id ? (
          <div className="flex items-center gap-2">
            <span>{row.original.child_company_name}</span>
            {row.original.child_is_archived && (
              <Badge variant="secondary" className="text-xs">
                archived
              </Badge>
            )}
          </div>
        ) : (
          // Not an empty cell. A provider with an allowance and no clients yet
          // is a real state a super admin needs to see.
          <span className="text-sm text-muted-foreground italic">
            none yet
          </span>
        ),
    },
    {
      accessorKey: 'usage',
      header: 'Allowance used',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className={row.original.at_limit ? 'font-medium' : ''}>
            {row.original.usage}
          </span>
          {row.original.at_limit && (
            <Badge variant="destructive" className="text-xs">
              at limit
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'child_created_at',
      header: 'Client since',
      cell: ({ row }) =>
        row.original.child_created_at ? (
          <span className="text-sm text-muted-foreground">
            {format(new Date(row.original.child_created_at), 'MMM d, yyyy')}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ]

  const filters: DataTableFilter[] = [
    {
      column: 'parent_company_name',
      type: 'text',
      label: 'Provider',
      placeholder: 'Filter by provider',
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Account hierarchy</h1>
          <p className="text-muted-foreground mt-1">
            Every provider and client account pairing on the platform, with the
            allowance each provider holds
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchPairings} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/security/audit')}>
            <History className="mr-2 h-4 w-4" />
            Change history
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Providers</p>
              <p className="text-2xl font-bold">{providerCount}</p>
            </div>
            <Building2 className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Client accounts
              </p>
              <p className="text-2xl font-bold">{clientCount}</p>
            </div>
            <Network className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">At their limit</p>
              <p className="text-2xl font-bold">{atLimitCount}</p>
            </div>
            {atLimitCount > 0 ? (
              <Badge variant="destructive" className="h-6">
                at limit
              </Badge>
            ) : (
              <Badge variant="outline" className="h-6">
                room
              </Badge>
            )}
          </div>
        </div>
      </div>

      <DataTableAdvanced
        columns={columns}
        data={rows}
        filters={filters}
        loading={loading}
        searchable
        searchPlaceholder="Search providers and client accounts..."
        searchKeys={['parent_company_name', 'child_company_name']}
        emptyMessage="No provider holds sub-accounts yet."
        pageSize={25}
      />
    </div>
  )
}
